import React, { useState } from 'react';
import { CheckCircle, AlertTriangle, XCircle, Info } from 'lucide-react';
import styles from './AIIntelligenceCards.module.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faExclamation } from '@fortawesome/free-solid-svg-icons';
import AIIntelligenceModal from './AIIntelligenceModal';

const AIIntelligenceCards = ({ cards, title = "AI Claims Intelligence", sub_title = "AI-powered insights to guide your decision-making" }) => {
  const basePath = process.env.PUBLIC_URL || '';
  const [selectedCard, setSelectedCard] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleCardClick = (card) => {
    setSelectedCard(card);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedCard(null);
  };

  // Generate unique key for each card
  const getUniqueCardKey = (card) => {
    const entityCode = card.entityConfig?.code || '';
    const entityId = card.entityId || card.rawApiData?.entityId || '';
    const id = card.id || '';
    return `${entityCode}-${entityId}-${id}`;
  };

  // Use only cards from props, filter out cards with errors and duplicates
  const cardsToRender = (cards || []).filter((card, index, self) => {
    // Filter out cards that have error messages
    if (card.rawApiData?.error_msg) return false;
    
    // Filter out duplicates based on entityConfig.code
    const uniqueKey = card.entityConfig?.code;
    if (!uniqueKey) return true; // Keep cards without entityConfig.code
    
    const firstIndex = self.findIndex(c => c.entityConfig?.code === uniqueKey);
    
    return index === firstIndex;
  });

  const renderCard = (card) => {
    const cardType = card.entityConfig?.code || card.template;
    
    switch (cardType) {
      case "AI_CARD_CLAIM_UNDERSTANDING":
      case "claimUnderstanding":
        const percentage = parseInt(card.metric) || 100;
        const circumference = 2 * Math.PI * 60;
        const offset = circumference - (percentage / 100) * circumference;
        
        return (
          <div key={getUniqueCardKey(card)} className={styles.claimUnderstandingCard}>
            <div className={styles.cardHeader}>
              <div className={styles.cardHeaderIcon}>
                <img src={`${basePath}/assets/images/workbench/Claim_understanding.jpg`} alt="Claim Understanding" style={{ width: '30px', height: '30px' }} />
              </div>
              <div className={styles.cardHeaderContent}>
                <div className={styles.cardHeaderTitle}>{card.title}</div>
                <div className={styles.cardHeaderSubtitle}>
                  <img src={`${basePath}/assets/images/workbench/ai-star-icon-grey.png`} alt="subheader-icon" style={{ width: '16px', height: '16px' }} />
                  {card.subtitle}
                </div>
              </div>
            </div>
            
            <div className={styles.circularProgressContainer}>
              <div className={styles.circularProgressWrapper}>
                <svg width="100" height="100" viewBox="0 0 100 100">
                  <circle
                    cx="50"
                    cy="50"
                    r="43"
                    fill="none"
                    stroke="#E5E7EB"
                    strokeWidth="6"
                  />
                  <circle
                    cx="50"
                    cy="50"
                    r="43"
                    fill="none"
                    stroke="#00a1ff"
                    strokeWidth="6"
                    strokeLinecap="round"
                    strokeDasharray={circumference}
                    strokeDashoffset={offset}
                    transform="rotate(-90 50 50)"
                    style={{ transition: 'stroke-dashoffset 1s ease' }}
                  />
                </svg>
                <div className={styles.circularProgressText}>
                  <span className={styles.circularProgressValue}>{card.metric}</span>
                </div>
                {percentage === 100 && (
                  <img 
                    src={`${basePath}/assets/images/workbench/Badge_Check.svg`} 
                    alt="Complete" 
                    style={{ 
                      width: '32px', 
                      height: '32px',
                      position: 'absolute',
                      top: '5px',
                      right: '0px'
                    }} 
                  />
                )}
              </div>
              <div className={styles.circularProgressLabel}>{card.description}</div>
            </div>
            
            <div className={styles.keySignalsSection}>
              <div className={styles.keySignalsTitle}>Key Signals</div>
              {card.keySignalsText && card.keySignalsText.split("- ").filter(signal => signal.trim()).map((signal, index) => (
                <div key={index} className={styles.keySignalItem}>
                  {signal.replace("✅ ", "✓ ")}
                </div>
              ))}
            </div>
            
            <button className={styles.viewButton} onClick={() => handleCardClick(card)}>{card.buttonText}</button>
          </div>
        );
      
      case "AI_CARD_POLICY_RULES_INSIGHT":
      case "policyRules":
        const policyPercentage = parseInt(card.metric) || 80;
        const policyCircumference = 2 * Math.PI * 60;
        const policyOffset = policyCircumference - (policyPercentage / 100) * policyCircumference;
        
        return (
          <div key={getUniqueCardKey(card)} className={styles.policyRulesCard}>
            <div className={styles.cardHeader}>
              <div className={styles.cardHeaderIcon}>
                <img src={`${basePath}/assets/images/workbench/Policy.jpg`} alt="Policy Rules" style={{ width: '30px', height: '30px' }} />
              </div>
              <div className={styles.cardHeaderContent}>
                <div className={styles.cardHeaderTitle}>{card.title}</div>
                <div className={styles.cardHeaderSubtitle}>
                  <img src={`${basePath}/assets/images/workbench/ai-star-icon-grey.png`} alt="subheader-icon" style={{ width: '16px', height: '16px' }} />
                  {card.subtitle}
                </div>
              </div>
            </div>
            
            <div className={styles.circularProgressContainer}>
              <div className={styles.circularProgressWrapper}>
                <svg width="100" height="100" viewBox="0 0 100 100">
                  <circle
                    cx="50"
                    cy="50"
                    r="43"
                    fill="none"
                    stroke="#E5E7EB"
                    strokeWidth="6"
                  />
                  <circle
                    cx="50"
                    cy="50"
                    r="43"
                    fill="none"
                    stroke="#00a1ff"
                    strokeWidth="6"
                    strokeLinecap="round"
                    strokeDasharray={policyCircumference}
                    strokeDashoffset={policyOffset}
                    transform="rotate(-90 50 50)"
                    style={{ transition: 'stroke-dashoffset 1s ease' }}
                  />
                </svg>
                <div className={styles.circularProgressText}>
                  <span className={styles.circularProgressValue}>{card.metric}</span>
                </div>
              </div>
              <div className={styles.circularProgressLabel}>{card.description}</div>
            </div>
            
            <div className={styles.keySignalsSection}>
              <div className={styles.keySignalsTitle}>Key Signals</div>
              {card.keySignalsText && card.keySignalsText.split("- ").filter(signal => signal.trim()).map((signal, index) => (
                <div key={index} className={styles.keySignalItem}>
                  {signal.replace("✅ ", "✓ ")}
                </div>
              ))}
            </div>
            
            <button className={styles.viewButton} onClick={() => handleCardClick(card)}>{card.buttonText}</button>
          </div>
        );
      
      case "AI_CARD_CLINICAL_INTELLIGENCE":
      case "clinicalIntelligence":
        const getClinicalSeverityIcon = (severity) => {
          const iconMap = {
            'success': <CheckCircle size={20} />,
            'warning': <FontAwesomeIcon icon={faExclamation} /> ,
            'error': <XCircle size={20} />,
            'info': <Info size={20} />
          };
          return iconMap[severity?.toLowerCase()] || <Info size={20} />;
        };

        const getClinicalSeverityColor = (severity) => {
          const colorMap = {
            'success': '#00b928',
            'warning': '#e8a900',
            'error': '#ff3e42',
            'info': '#00a1ff'
          };
          return colorMap[severity?.toLowerCase()] || '#00a1ff';
        };

        const clinicalSeverity = card.rawApiData?.statusSeverity;
        const clinicalColor = getClinicalSeverityColor(clinicalSeverity);

        return (
          <div key={getUniqueCardKey(card)} className={styles.clinicalIntelligenceCard}>
            <div className={styles.cardHeader}>
              <div className={styles.cardHeaderIcon}>
                {/* <img src="/assets/images/workbench/Clinical_intelligence.jpg" alt="Clinical Intelligence" style={{ width: '30px', height: '30px' }} /> */}
                <img src={`${basePath}/assets/images/workbench/Clinical_intelligence.jpg`} alt="Clinical Intelligence" style={{ width: '30px', height: '30px' }} />

              </div>
              <div className={styles.cardHeaderContent}>
                <div className={styles.cardHeaderTitle}>{card.title}</div>
                <div className={styles.cardHeaderSubtitle}>
                    <img src={`${basePath}/assets/images/workbench/ai-star-icon-grey.png`} alt="subheader-icon" style={{ width: '16px', height: '16px' }} />
                  {card.subtitle}
                  </div>
              </div>
            </div>
            
            <div className={styles.riskBadgeSection} style={{ background: `linear-gradient(0deg, ${clinicalColor}15 0%, #ffffff 100%)` }}>
              <div className={styles.riskBadge} style={{ backgroundColor: clinicalColor, color: 'white' }}>
                {getClinicalSeverityIcon(clinicalSeverity)}
              </div>
              <div className={styles.riskLevel}>{card.rawApiData?.primaryMetricValueText}</div>
            </div>
            
            <div className={styles.clinicalSignalsSection}>
              <div className={styles.clinicalSignalsTitle}>Key Signals</div>
              {card.keySignalsText && card.keySignalsText.split("- ").map((signal, index) => (
                <div key={index} className={styles.clinicalSignalItem}>
                  {signal}
                </div>
              ))}
            </div>
            
            <button className={styles.viewButton} onClick={() => handleCardClick(card)}>{card.buttonText}</button>
          </div>
        );
      
      case "AI_CARD_UTILIZATION_BILLING_INSIGHT":
      case "utilizationBilling":
        const getUtilizationSeverityIcon = (severity) => {
          const iconMap = {
            'success': <CheckCircle size={20} />,
            'warning': <FontAwesomeIcon icon={faExclamation} />,
            'error': <XCircle size={20} />,
            'info': <Info size={20} />
          };
          return iconMap[severity?.toLowerCase()] || <Info size={20} />;
        };

        const getUtilizationSeverityColor = (severity) => {
          const colorMap = {
            'success': '#00b928',
            'warning': '#e8a900',
            'error': '#ff3e42',
            'info': '#00a1ff'
          };
          return colorMap[severity?.toLowerCase()] || '#00a1ff';
        };

        const utilizationSeverity = card.rawApiData?.statusSeverity;
        const utilizationColor = getUtilizationSeverityColor(utilizationSeverity);

        return (
          <div key={getUniqueCardKey(card)} className={styles.utilizationBillingCard}>
            <div className={styles.cardHeader}>
              <div className={styles.cardHeaderIcon}>
                <img src={`${basePath}/assets/images/workbench/Utilization_billing.jpg`} alt="Utilization Billing" style={{ width: '30px', height: '30px' }} />
              </div>
              <div className={styles.cardHeaderContent}>
                <div className={styles.cardHeaderTitle}>
                  Utilization & Billing Insight Agent
                </div>
                <div className={styles.cardHeaderSubtitle}>
                  <img src={`${basePath}/assets/images/workbench/ai-star-icon-grey.png`} alt="subheader-icon" style={{ width: '16px', height: '16px' }} />
                  {card.cardSubtitle || card.subtitle}
                  
                </div>
              </div>
            </div>
            
            <div className={styles.mediumRiskBadgeSection} style={{ background: `linear-gradient(0deg, ${utilizationColor}15 0%, #ffffff 100%)` }}>
              <div className={styles.mediumRiskBadge} style={{ backgroundColor: utilizationColor, color: 'white' }}>
                {getUtilizationSeverityIcon(utilizationSeverity)}
              </div>
              <div className={styles.riskLevel}>{card.riskLevel || card.primaryMetricValueText || card.metric}</div>
            </div>
            
            {card.keySignalsText && (
              <div className={styles.keySignalsSection}>
                <div className={styles.keySignalsTitle}>Key Signals</div>
                {card.keySignalsText.split("- ").filter(item => item.trim()).map((signal, index) => (
                  <div key={index} className={styles.keySignalItem}>
                    {signal.trim()}
                  </div>
                ))}
              </div>
            )}
            
            {(card.varianceAmount || card.varianceLabel || card.description || card.rawApiData?.varianceAmount || card.rawApiData?.varianceLabel) && (
              <div className={styles.varianceBox}>
                {(card.varianceLabel || card.rawApiData?.varianceLabel) && (
                  <div className={styles.varianceLabel}>{card.varianceLabel || card.rawApiData?.varianceLabel}</div>
                )}
                <div className={styles.varianceAmount}>{card.varianceAmount || card.rawApiData?.varianceAmount || card.description}</div>
              </div>
            )}
            
            <button className={styles.viewButton} onClick={() => handleCardClick(card)}>{card.ctaText || card.buttonText}</button>
          </div>
        );
      
      case "AI_CARD_CLAIM_SYNTHESIS":
      case "claimSynthesis":
        const getSeverityClass = (severity) => {
          const severityMap = {
            'success': styles.statusSuccess,
            'warning': styles.statusWarning,
            'error': styles.statusError,
            'info': styles.statusInfo
          };
          return severityMap[severity?.toLowerCase()] || styles.statusInfo;
        };
        return (
          <div key={getUniqueCardKey(card)} className={styles.claimSynthesisCard}>
            <div className={styles.cardHeader}>
              <div className={styles.cardHeaderIcon}>
                <img src={`${basePath}/assets/images/workbench/Claim_synthesis.jpg`} alt="Claim Synthesis" style={{ width: '30px', height: '30px' }} />
              </div>
              <div className={styles.cardHeaderContent}>
                <div className={styles.cardHeaderTitle}>{card.cardTitle || card.title}</div>
                <div className={styles.cardHeaderSubtitle}>
                  <img src={`${basePath}/assets/images/workbench/ai-star-icon-grey.png`} alt="subheader-icon" style={{ width: '16px', height: '16px' }} />
                  {card.cardSubtitle || card.subtitle}
                </div>
              </div>
            </div>
            
            
            <div className={styles.primaryMetricSection}>
              <div className={styles.primaryMetricLabel}>{card.primaryMetricLabel || "Final Payable"}</div>
              <div className={styles.primaryMetricValue}>{card.primaryMetricValueText || card.finalPayableAmount || card.metric}</div>
            </div>
            
            {card.keySignalsText && (
              <div className={styles.keySignalsSection}>
                <div className={styles.keySignalsTitle}>Key Signals</div>
                {card.keySignalsText.split('- ').map((signal, index) => (
                  signal.trim() && (
                    <div key={index} className={styles.keySignalItem}>
                      {signal}
                    </div>
                  )
                ))}
              </div>
            )}
            
            {card.rawApiData?.statusLabel && (
              <div className={styles.statusBadgeSection}>
                <div className={`${styles.statusBadge} ${getSeverityClass(card.rawApiData?.statusSeverity)}`}>
                  {card.rawApiData?.statusLabel}
                </div>
              </div>
            )}
            <button className={styles.viewButton} onClick={() => handleCardClick(card)}>{card.ctaText || card.buttonText}</button>
          </div>
        );
      
      case "AI_CARD_RISK_CONFIDENCE":
      case "riskConfidence":
        const confidencePercentage = parseInt(card.confidencePercent || card.confidenceScore) || 92;
        const confidenceCircumference = 2 * Math.PI * 60;
        const confidenceOffset = confidenceCircumference - (confidencePercentage / 100) * confidenceCircumference;
        
        return (
          <div key={getUniqueCardKey(card)} className={styles.riskConfidenceCard}>
            <div className={styles.cardHeader}>
              <div className={styles.cardHeaderIcon}>
                <img src={`${basePath}/assets/images/workbench/Risk_confidence.jpg`} alt="Risk Confidence" style={{ width: '30px', height: '30px' }} />
              </div>
              <div className={styles.cardHeaderContent}>
                <div className={styles.cardHeaderTitle}>{card.cardTitle || card.title}</div>
                <div className={styles.cardHeaderSubtitle}>
                  <img src={`${basePath}/assets/images/workbench/ai-star-icon-grey.png`} alt="subheader-icon" style={{ width: '16px', height: '16px' }} />
                  {card.cardSubtitle || card.subtitle}
                </div>
              </div>
            </div>
            
            <div className={styles.circularProgressContainer}>
              <div className={styles.circularProgressWrapper}>
                <svg width="100" height="100" viewBox="0 0 100 100">
                  <circle
                    cx="50"
                    cy="50"
                    r="43"
                    fill="none"
                    stroke="#E5E7EB"
                    strokeWidth="6"
                  />
                  <circle
                    cx="50"
                    cy="50"
                    r="43"
                    fill="none"
                    stroke="#00b928"
                    strokeWidth="6"
                    strokeLinecap="round"
                    strokeDasharray={confidenceCircumference}
                    strokeDashoffset={confidenceOffset}
                    transform="rotate(-90 50 50)"
                    style={{ transition: 'stroke-dashoffset 1s ease' }}
                  />
                </svg>
                <div className={styles.circularProgressText}>
                  {confidencePercentage === 100 ? (
                    <img src={`${basePath}/assets/images/workbench/Badge_Check.svg`} alt="Complete" style={{ width: '50px', height: '50px' }} />
                  ) : (
                    <span className={styles.circularProgressValue}>{card.primaryMetricValueText || card.confidenceScore || `${confidencePercentage}%`}</span>
                  )}
                </div>
              </div>
              <div className={styles.circularProgressLabel}>{card.primaryMetricLabel || card.confidenceLabel || card.description}</div>
            </div>
            
            {card.keySignalsText && (
              <div className={styles.keySignalsSection}>
                <div className={styles.keySignalsTitle}>Key Signals</div>
                {card.keySignalsText.split('\n').map((signal, index) => (
                  signal.trim() && (
                    <div key={index} className={styles.keySignalItem}>
                      {signal}
                    </div>
                  )
                ))}
              </div>
            )}
            
            <button className={styles.viewButton} onClick={() => handleCardClick(card)}>{card.ctaText || card.buttonText}</button>
          </div>
        );
      
      case "withMetric":
        return (
          <div key={getUniqueCardKey(card)} className={styles.intelligenceCard}>
            <div className={styles.iconContainer}>{card.icon}</div>
            <div className={styles.cardTitle}>{card.title}</div>
            {card.metric && <div className={styles.cardMetric}>{card.metric}</div>}
            <div className={styles.cardDescription}>{card.description}</div>
          </div>
        );
      
      case "withoutMetric":
        return (
          <div key={getUniqueCardKey(card)} className={styles.intelligenceCard}>
            <div className={styles.iconContainer}>{card.icon}</div>
            <div className={styles.cardTitle}>{card.title}</div>
            <div className={styles.cardDescription}>{card.description}</div>
          </div>
        );
      
      default:
        // Generic fallback card template
        const displayName = card.entityConfig?.displayName || card.entityConfig?.name || card.title || 'AI Intelligence Card';
        const subtitle = card.subtitle || card.entityConfig?.description || 'AI-powered insights';
        const primaryMetric = card.metric || card.rawApiData?.recommendedPayableAmountText || null;
        const metricLabel = card.description || card.rawApiData?.entityType || 'Analysis Result';
        
        return (
          <div key={getUniqueCardKey(card)} className={styles.claimUnderstandingCard} onClick={() => handleCardClick(card)}>
            <div className={styles.cardHeader}>
              <div className={styles.cardHeaderIcon}>
                <span style={{ fontSize: '24px' }}>{card.icon || '📊'}</span>
              </div>
              <div className={styles.cardHeaderContent}>
                <div className={styles.cardHeaderTitle}>{displayName}</div>
                <div className={styles.cardHeaderSubtitle}>
                  <img src={`${basePath}/assets/images/workbench/ai-star-icon-grey.png`} alt="subheader-icon" style={{ width: '16px', height: '16px' }} />
                  {subtitle}
                </div>
              </div>
            </div>
            
            {primaryMetric && (
              <div className={styles.primaryMetricSection}>
                <div className={styles.primaryMetricLabel}>{card.primaryMetricLabel || "Final Payable"}</div>
                <div className={styles.primaryMetricValue}>{card.primaryMetricValueText || card.finalPayableAmount || card.metric}</div>
                {/* <div className={styles.primaryMetricValue}>{primaryMetric}</div> */}
              </div>
              // <div className={styles.circularProgressContainer}>
              //   <div className={styles.circularProgressWrapper}>
              //     <div className={styles.circularProgressText} style={{ position: 'relative', fontSize: '1.5rem', fontWeight: '600', color: '#111827' }}>
              //       {primaryMetric}
              //     </div>
              //   </div>
              //   <div className={styles.circularProgressLabel}>{metricLabel}</div>
              // </div>
            )}
            
            {card.keySignalsText && (
              <div className={styles.keySignalsSection}>
                <div className={styles.keySignalsTitle}>Key Signals</div>
                {card.keySignalsText.split("- ").filter(signal => signal.trim()).map((signal, index) => (
                  <div key={index} className={styles.keySignalItem}>
                    {signal.replace("✅ ", "✓ ")}
                  </div>
                ))}
              </div>
            )}
            
            {card.rawApiData?.summary && !card.keySignalsText && (
              <div className={styles.keySignalsSection}>
                <div className={styles.clinicalSignalsTitle} style={{ fontSize: '0.75rem', fontWeight: '600', color: '#6b7280', marginBottom: '0.5rem' }}>Summary</div>
                <div 
                  style={{ fontSize: '0.875rem', color: '#374151', lineHeight: '1.5', cursor: 'help' }}
                  title={card.rawApiData.summary}
                >
                  {card.rawApiData.summary.substring(0, 150)}...
                </div>
              </div>
            )}
            
            <button className={styles.viewButton} onClick={(e) => { e.stopPropagation(); handleCardClick(card); }}>
              {card.buttonText || 'View Details'}
            </button>
          </div>
        );
    }
  };

  return (
    <>
      <div className={styles.intelligenceSection}>
        <h3 className={styles.sectionTitle}>{title}</h3>
        <div className={styles.subSectionTitle}>{sub_title}</div>
        <div className={styles.intelligenceGrid}>
          {cardsToRender.map(card => renderCard(card))}
        </div>
      </div>
      
      <AIIntelligenceModal 
        isOpen={isModalOpen} 
        onClose={handleCloseModal} 
        card={selectedCard} 
      />
    </>
  );
};

export default AIIntelligenceCards;
